import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FormDrawer from 'components/drawers/form-drawer/form-drawer.component';
import { IncDrawerType } from 'components/drawers/inc-drawer/inc-drawer.component';
import CreateRelatedLocationForm from 'components/forms/create-related-location-form/create-related-location-form.component';
import { useCreateRelatedLocationForm } from 'components/forms/create-related-location-form/create-related-location-form.hooks';
import { useCreateLocationForm } from 'components/forms/location/location-form.hooks';
import { StoreResult } from 'api/client-domain/client-domain.types';
import { StoreOrderingForm } from 'api/ordering-service/ordering-service.types';
import { useSnackbar } from 'notistack';
import { useGlobalDrawer } from 'components/drawers/global-drawer/global-drawer.hooks';

export type CreateRelatedLocationDrawerDataType = {
  locationId: string;
  store?: StoreResult;
  ordering?: StoreOrderingForm;
};

type CreateRelatedLocationDrawerProps =
  IncDrawerType<CreateRelatedLocationDrawerDataType>;

const CreateRelatedLocationDrawer = ({
  close,
  data: drawerData,
  isOpen,
}: CreateRelatedLocationDrawerProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { form } = useCreateRelatedLocationForm({
    displayInfo: drawerData?.store.displayInfo,
  });
  const { reset, clearErrors } = form;

  const {
    submit,
    isStoreLoading,
    isOrderingLoading,
    isStoreSuccess,
    isOrderingSuccess,
  } = useCreateLocationForm();

  const handleClose = useCallback(() => {
    close();
    reset();
    clearErrors();
  }, [clearErrors, close, reset]);

  const { close: closeRelatedLocationDrawer } = useGlobalDrawer(
    'globalRelatedLocationsDrawer',
  );

  const onValid = async data => {
    try {
      const { locationId, store, ordering } = drawerData;

      const modifiedStore = {
        ...store,
        title: data.relatedLocationName,
        isCatering: data.isCatering,
        displayInfo: data.displayInfo,
      };

      const modifiedOrdering = {
        ...ordering,
        locationName: data.relatedLocationName,
        primaryLocation: false,
        primaryLocationId: locationId,
        primaryStore: store,
      };

      const body = {
        store: modifiedStore,
        ordering: modifiedOrdering,
      };

      await submit(body);
    } catch (_e) {}
  };

  useEffect(() => {
    if (isStoreSuccess && isOrderingSuccess) {
      enqueueSnackbar(
        t('drawer.createRelatedLocationDrawer.toast.createRelatedLocation'),
        {
          severity: 'success',
        },
      );
      closeRelatedLocationDrawer();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStoreSuccess, isOrderingSuccess]);

  return (
    <FormDrawer
      title={t('drawer.createRelatedLocationDrawer.title')}
      subtitle={t('drawer.createRelatedLocationDrawer.subtitle')}
      open={isOpen}
      onValid={onValid}
      cancel={handleClose}
      isLoading={isStoreLoading || isOrderingLoading}
      formMethods={form}
      data-testid="CreateRelatedLocationDrawer.FormDrawer"
      onClose={handleClose}
      submitButtonLabel={t('drawer.createRelatedLocationDrawer.buttons.submit')}
    >
      <CreateRelatedLocationForm />
    </FormDrawer>
  );
};

export default CreateRelatedLocationDrawer;
